import * as Joi from 'joi';
import _ from 'lodash';
import { CLIENT_STATE_KEYS, CLIENT_STATE_VERSIONS, DEFAULT_AXIS_EXTREMES, DEFAULT_PIVOT_STATE, DEFAULT_RANGE_PARAMS, getDefaultPropsFromDefaultValues, getUpdatedFormatConfig, } from '../../client-state-type';
import { DEFAULT_CONDITIONAL_METRIC } from '../../conditional-formatting-upgrade-util';
import { ChartVizPropVersion, } from '../../types/prism';
import { DEFAULT_AXIS_PROPERTIES } from '../chart-axis';
import { ClientStateV4Schema } from '../types/v4';
import { ClientStateV4dot1Class, DEFAULT_CHART_SPECIFICV4dot1, } from '../types/v4dot1';
import { DeprecatedToResponsiveLayoutMap, getUpdatedColumnPropertiesForV4, getUpdatedMultiSeriesColorForV4, getUpdatedPivotFields, getUpdatedSeriesColorForV4, } from './v3-to-v4dot1';
const parseConditionalFormattingFromClientStateToVizProp = (columnProperty, defaultMetric = DEFAULT_CONDITIONAL_METRIC) => {
    if (_.isNil(columnProperty.conditionalFormatting) ||
        _.isNil(columnProperty.conditionalFormatting.rows) ||
        _.isEmpty(columnProperty.conditionalFormatting.rows)) {
        return null;
    }
    return {
        __typename: 'ConditionalFormatting',
        rows: _.get(columnProperty, [CLIENT_STATE_KEYS.conditionalFormatting, 'rows'], []).map(metric => {
            const result = getDefaultPropsFromDefaultValues(defaultMetric, metric);
            if (metric.solidBackgroundAttrs) {
                result.solidBackgroundAttrs = {
                    ...result.solidBackgroundAttrs,
                    __typename: 'SolidBackgroundAttrs',
                };
            }
            if (metric.rangeValues) {
                result.rangeValues = {
                    ...result.rangeValues,
                    __typename: 'Range',
                };
            }
            if (metric.fontProperties) {
                result.fontProperties = {
                    ...result.fontProperties,
                    __typename: 'FontAttrs',
                };
            }
            if (metric.gradientBackgroundAttrs) {
                result.gradientBackgroundAttrs = {
                    ...result.gradientBackgroundAttrs,
                    __typename: 'GradientBackgroundAttrs',
                };
            }
            return result;
        }),
    };
};
function getUpdatedPivotPaths(pivotStatePaths) {
    if (!pivotStatePaths || !pivotStatePaths.length) {
        return [];
    }
    return pivotStatePaths.map(paths => {
        return {
            __typename: 'PivotPaths',
            paths: paths.paths,
        };
    });
}
function getPivotStatePropsFromDXPivotState(pivotState) {
    return {
        ...DEFAULT_PIVOT_STATE,
        columnExpandedPaths: getUpdatedPivotPaths(pivotState.columnExpandedPaths),
        rowExpandedPaths: getUpdatedPivotPaths(pivotState.rowExpandedPaths),
        fields: getUpdatedPivotFields(pivotState.fields),
    };
}
const getUpgradedAxisPropertiesForV4dot1 = (clientState) => {
    const axisProperties = _.get(clientState, CLIENT_STATE_KEYS.axisProperties);
    if (!Array.isArray(axisProperties)) {
        throw new Error('schema doesnt match for the axis properties in v4 to v4dot1 flow');
    }
    const updatedAxisProperties = [];
    axisProperties.forEach((axisObj) => {
        var _a, _b, _c;
        updatedAxisProperties.push({
            __typename: 'AxisObj',
            id: axisObj.id,
            properties: {
                ...getDefaultPropsFromDefaultValues(DEFAULT_AXIS_PROPERTIES, axisObj.properties),
                yAxisRange: axisObj.properties.yAxisRange
                    ? {
                        __typename: 'RangeParam',
                        max: (_a = axisObj.properties.yAxisRange.max) !== null && _a !== void 0 ? _a : null,
                        min: (_b = axisObj.properties.yAxisRange.min) !== null && _b !== void 0 ? _b : null,
                    }
                    : null,
                format: getUpdatedFormatConfig(axisObj.properties.format),
                customProps: (_c = axisObj.properties.customProps) !== null && _c !== void 0 ? _c : null,
            },
        });
    });
    return updatedAxisProperties;
};
const getUpgradedChartPropertiesForV4 = (clientState) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    const chartProperties = _.get(clientState, CLIENT_STATE_KEYS.chartProperties);
    return chartProperties
        ? {
            allLabels: (_a = chartProperties.allLabels) !== null && _a !== void 0 ? _a : null,
            axisExtremes: chartProperties.axisExtremes
                ? {
                    ...DEFAULT_AXIS_EXTREMES,
                    x: chartProperties.axisExtremes.x
                        ? chartProperties.axisExtremes.x.map(rangeParam => {
                            var _a, _b;
                            return ({
                                ...DEFAULT_RANGE_PARAMS,
                                max: (_a = rangeParam.max) !== null && _a !== void 0 ? _a : null,
                                min: (_b = rangeParam.min) !== null && _b !== void 0 ? _b : null,
                            });
                        })
                        : [],
                    y: chartProperties.axisExtremes.y
                        ? chartProperties.axisExtremes.y.map(rangeParam => {
                            var _a, _b;
                            return ({
                                ...DEFAULT_RANGE_PARAMS,
                                max: (_a = rangeParam.max) !== null && _a !== void 0 ? _a : null,
                                min: (_b = rangeParam.min) !== null && _b !== void 0 ? _b : null,
                            });
                        })
                        : [],
                }
                : null,
            dataSize: (_b = chartProperties.dataSize) !== null && _b !== void 0 ? _b : null,
            gridLines: chartProperties.gridLines
                ? {
                    __typename: 'GridLineProperties',
                    xGridlineEnabled: (_c = chartProperties.gridLines.xGridlineEnabled) !== null && _c !== void 0 ? _c : null,
                    yGridlineEnabled: (_d = chartProperties.gridLines.yGridlineEnabled) !== null && _d !== void 0 ? _d : null,
                }
                : null,
            isZoomed: (_e = chartProperties.isZoomed) !== null && _e !== void 0 ? _e : null,
            mapviewport: chartProperties.mapviewport
                ? {
                    __typename: 'MapViewport',
                    center: (_f = chartProperties.mapviewport.center) !== null && _f !== void 0 ? _f : null,
                    zoomLevel: (_g = chartProperties.mapviewport.zoomLevel) !== null && _g !== void 0 ? _g : null,
                }
                : null,
            responsiveLayoutDisabled: (_h = chartProperties.responsiveLayoutDisabled) !== null && _h !== void 0 ? _h : null,
            showLinearRegressionLine: (_j = chartProperties.showLinearRegressionLine) !== null && _j !== void 0 ? _j : null,
            showStackedLabels: (_k = chartProperties.showStackedLabels) !== null && _k !== void 0 ? _k : null,
            visibleSeriesNames: chartProperties.visibleSeriesNames || [],
            chartSpecific: chartProperties.chartSpecific
                ? {
                    ...DEFAULT_CHART_SPECIFICV4dot1,
                    hidePivotSummaries: (_l = chartProperties.chartSpecific.hidePivotSummaries) !== null && _l !== void 0 ? _l : null,
                    isHeatmapOverlayed: (_m = chartProperties.chartSpecific.isHeatmapOverlayed) !== null && _m !== void 0 ? _m : null,
                    isStackedAsPercent: (_o = chartProperties.chartSpecific.isStackedAsPercent) !== null && _o !== void 0 ? _o : null,
                    markersEnabled: (_p = chartProperties.chartSpecific.markersEnabled) !== null && _p !== void 0 ? _p : null,
                    summaryMode: (_q = chartProperties.chartSpecific.summaryMode) !== null && _q !== void 0 ? _q : null,
                    useFlatLayout: (_r = chartProperties.chartSpecific.useFlatLayout) !== null && _r !== void 0 ? _r : null,
                    pivotState: chartProperties.chartSpecific.pivotState
                        ? getPivotStatePropsFromDXPivotState(chartProperties.chartSpecific.pivotState)
                        : null,
                    summaryFormat: getUpdatedFormatConfig(chartProperties.chartSpecific.summaryFormat),
                    stackedAsPercentFormat: getUpdatedFormatConfig(chartProperties.chartSpecific
                        .stackedAsPercentFormat),
                }
                : null,
            responsiveLayoutPreference: chartProperties.responsiveLayoutPreference
                ? DeprecatedToResponsiveLayoutMap[chartProperties.responsiveLayoutPreference]
                : null,
            __typename: 'ChartProperties',
        }
        : null;
};
const getUpgradedColumnPropertiesForV4dot1 = (clientState, vizModel) => {
    const columnProperties = _.get(clientState, CLIENT_STATE_KEYS.columnProperties);
    if (_.isArray(columnProperties)) {
        return columnProperties
            .filter(obj => !_.isEmpty(obj.columnProperty))
            .map(obj => ({
            __typename: 'ChartColumnPropertyMap',
            columnId: obj.columnId,
            columnProperty: {
                __typename: 'ChartVizColumnProperties',
                dataLabels: _.get(obj.columnProperty, [CLIENT_STATE_KEYS.dataLabels], null),
                conditionalFormatting: parseConditionalFormattingFromClientStateToVizProp(obj.columnProperty),
            },
        }));
    }
    return getUpdatedColumnPropertiesForV4(clientState, vizModel);
};
const getUpgradedSeriesColors = (clientState, clientStateKey) => {
    const seriesColors = _.get(clientState, clientStateKey, []);
    if (_.isArray(seriesColors)) {
        return seriesColors.map((color) => {
            color.__typename = 'SeriesColor';
            return color;
        });
    }
    return getUpdatedSeriesColorForV4(clientState, clientStateKey);
};
const getMultiSeriesColorWithTypeName = (multiSeriesColors) => {
    return multiSeriesColors.map(seriesColor => {
        return {
            ...seriesColor,
            __typename: 'MultiSeriesColorMap',
            colorMap: seriesColor.colorMap
                ? seriesColor.colorMap.map(colorMap => {
                    return {
                        ...colorMap,
                        __typename: 'SeriesMultiColorMap',
                    };
                })
                : [],
        };
    });
};
const getUpgradedMultiSeriesColors = (clientState, clientStateKey) => {
    const multiSeriesColors = _.get(clientState, clientStateKey, []);
    if (_.isArray(multiSeriesColors)) {
        return getMultiSeriesColorWithTypeName(multiSeriesColors);
    }
    return getUpdatedMultiSeriesColorForV4(clientState, clientStateKey);
};
const upgradeV4ToV4dot1 = (clientState, vizModel) => {
    const updatedClientState = {
        __typename: 'ChartVizProps',
        axisProperties: getUpgradedAxisPropertiesForV4dot1(clientState),
        chartProperties: getUpgradedChartPropertiesForV4(clientState),
        columnProperties: getUpgradedColumnPropertiesForV4dot1(clientState, vizModel),
        customColorSelectorArray: _.get(clientState, CLIENT_STATE_KEYS.customColorSelectorArray, []),
        seriesColors: getUpgradedSeriesColors(clientState, CLIENT_STATE_KEYS.SERIES_COLORS),
        systemSeriesColors: getUpgradedSeriesColors(clientState, CLIENT_STATE_KEYS.SYSTEM_SERIES_COLORS),
        multiColorSeriesColors: getUpgradedMultiSeriesColors(clientState, CLIENT_STATE_KEYS.MULTI_COLOR_SERIES_COLORS),
        systemMultiColorSeriesColors: getUpgradedMultiSeriesColors(clientState, CLIENT_STATE_KEYS.SYSTEM_MULTI_COLOR_SERIES_COLORS),
        version: ChartVizPropVersion.V4Dot1,
    };
    const clientStateV4dot1 = new ClientStateV4dot1Class();
    _.assign(clientStateV4dot1, updatedClientState);
    return clientStateV4dot1;
};
const upgraderFn = (clientState, vizModel) => {
    Joi.assert(clientState, ClientStateV4Schema);
    const clientStateVersion = _.get(clientState, CLIENT_STATE_KEYS.version);
    if (clientStateVersion !== CLIENT_STATE_VERSIONS.V4) {
        throw new Error(`incompatible client state received in chart upgrade path for v4 to v4.1 with version no:${clientStateVersion}`);
    }
    return upgradeV4ToV4dot1(clientState, vizModel);
};
const testHooks = {
    upgradeV4ToV4dot1,
};
export { upgraderFn, testHooks, parseConditionalFormattingFromClientStateToVizProp, getUpgradedAxisPropertiesForV4dot1, getUpgradedColumnPropertiesForV4dot1, getUpgradedChartPropertiesForV4, getUpgradedSeriesColors, getUpgradedMultiSeriesColors, };
//# sourceMappingURL=v4-to-v4dot1.js.map